/**
 * Runners Service
 * 
 * Service for making API calls related to runners.
 * Handles REST API calls and transforms responses to a consistent format.
 */

import service from './index'

const resource = service.resource('runners')

export default {
  /**
   * Get all runners with pagination and search
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 50)
   * @param {string} params.search - Search term (default: '')
   * @returns {Promise} API response in consistent format
   */
  async getAllRunners(params = {}) {
    const { page = 1, limit = 50, search = '' } = params
    
    try {
      // Call the existing API with correct parameters
      // The existing API uses 0-based page numbering and different parameter names
      const queryParams = {
        page: page - 1, // Convert to 0-based page numbering
        size: limit,
        sort: 'id,desc'
      }
      
      if (params.feedback_enabled) {
        queryParams.feedback_enabled = true;
      }
      
      // Add search parameters if provided
      if (search && search.trim()) {
        // Try common search parameter names that might work with the existing API
        queryParams.search = search.trim()
        // Also try other possible parameter names
        queryParams.q = search.trim()
        queryParams.query = search.trim()
      }
      
      console.log('Calling API with params:', queryParams)

      let response;
      if (params.feedback_enabled) {
        const nodeApiUrl = process.env.NODE_ENV === 'development' 
            ? 'http://localhost:4000/api' 
            : (process.env.EXPRESS_API || (process.env.API ? process.env.API.replace('8080', '4000') : 'https://api.liverunapp.com/api'))
        
        // Convert to 1-based pagination for Node API
        const nodeParams = { ...queryParams, page: page }
        
        response = await service.http.get(`${nodeApiUrl}/runners`, { params: nodeParams })
        // Wrap response for caller compatibility (expecting response.body)
        response = { body: response.data }
      } else {
        response = await resource.query(queryParams)
      }

      console.log('Raw API response:', response)
      
      // Transform the response to consistent format
      const transformedResponse = this.transformApiResponse(response.body, page)
      console.log('Transformed response:', transformedResponse)
      
      return {
        body: {
          success: true,
          data: {
            AllRunners: transformedResponse
          }
        }
      }
    } catch (error) {
      console.error('Error fetching runners:', error)
      throw error
    }
  },

  /**
   * Transform API response to consistent format
   * @param {Object} apiResponse - Original API response
   * @param {number} currentPage - Current page number (1-based)
   * @returns {Object} Transformed response in consistent format
   */
  transformApiResponse(apiResponse, currentPage) {
    if (!apiResponse || !apiResponse.content) {
      return {
        count: 0,
        pagination: { totalPages: 0 },
        runners: []
      }
    }

    const runners = apiResponse.content.map(runner => this.transformRunnerData(runner))

    return {
      count: apiResponse.totalElements || 0,
      pagination: {
        totalPages: apiResponse.totalPages || 0,
        currentPage: currentPage,
        hasNext: !apiResponse.last,
        hasPrev: !apiResponse.first
      },
      runners: runners
    }
  },

  /**
   * Transform individual runner data to consistent format
   * @param {Object} runner - Original runner data from API
   * @returns {Object} Transformed runner data
   */
  transformRunnerData(runner) {
    return {
      id: runner.id,
      name: runner.firstName || '',
      location: runner.lastName || '',
      gender: runner.gender || '',
      facebook: !!runner.socialLogin,
      height: runner.height || null,
      weight: runner.weight || null,
      profileText: runner.profileText || '',
      website: runner.website || '',
      
      user: {
        email: (runner.user && runner.user.email) || '',
        accountCreate: (runner.user && runner.user.resetDate) ? 
          new Date(runner.user.resetDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(/\//g, '/') : ''
      },
      
      membership: runner.memberShip ? {
        startDate: runner.memberShip.trialStartDate ? 
          new Date(runner.memberShip.trialStartDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        endDate: runner.memberShip.trialEndDate ? 
          new Date(runner.memberShip.trialEndDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        promoStartDate: (runner.memberShip.promoCode && runner.memberShip.promoCode.expirationDate) ? 
          new Date(runner.memberShip.trialStartDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        promoEndDate: (runner.memberShip.promoCode && runner.memberShip.promoCode.expirationDate) ? 
          new Date(runner.memberShip.promoCode.expirationDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        promoCode: (runner.memberShip.promoCode && runner.memberShip.promoCode.code) || null,
        expirationDate: (runner.memberShip.promoCode && runner.memberShip.promoCode.expirationDate) ? 
          new Date(runner.memberShip.promoCode.expirationDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null
      } : null,
      
      runner_report: {
        totalCompletedRuns: 0, // This data is not available in the current API
        lastLiveRun: null,     // This data is not available in the current API
        daysSinceLastLiveRun: 0 // This data is not available in the current API
      },
      
      promo_report: null // This data is not available in the current API
    }
  },

  /**
   * Get runner by ID
   * @param {number} id - Runner ID
   * @returns {Promise} API response
   */
  getRunnerById(id) {
    return resource.get({ id })
  },

  /**
   * Refresh/refetch runners data
   * Same as getAllRunners but with explicit naming for compatibility
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  refetch(params = {}) {
    return this.getAllRunners(params)
  },

  /**
   * Update runner password
   * @param {number} runnerId - Runner ID
   * @param {string} password - New password
   * @returns {Promise} API response
   */
  async updatePassword(runnerId, password) {
    try {
      // Use explicit port 4000 for Node API, bypassing the default base URL which points to Java API (8080)
      const nodeApiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:4000/api' 
        : (process.env.EXPRESS_API || process.env.API.replace('8080', '4000'))
        
      const response = await service.http.post(`${nodeApiUrl}/runners/${runnerId}/password`, { password })
      return response
    } catch (error) {
      console.error('Error updating password:', error)
      throw error
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/runnersService.js